import { create } from 'zustand';
import authService from '../services/authService';
import teamGranularService from '../services/teamGranularService';
import { useDosPasosStore } from './dosPasosStore';
import toast from 'react-hot-toast';

const useAuthStore = create((set, get) => ({
  user: null,
  partner: null,
  teamMember: null,
  effectivePermissions: [],
  isAuthenticated: false,
  isLoading: true,
  error: null,

  // Initialize auth state
  initialize: async () => {
    const token = localStorage.getItem('partnerToken');
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const response = await authService.validateToken();
      // La respuesta del endpoint de partners viene en response.data
      const validateData = response?.data || response;

      if (validateData?.user) {
        set({
          user: validateData.user,
          partner: validateData.partner,
          isAuthenticated: true,
          isLoading: false
        });
      } else {
        set({ isLoading: false, isAuthenticated: false });
      }
    } catch (error) {
      localStorage.removeItem('partnerToken');
      localStorage.removeItem('partnerRefreshToken');
      localStorage.removeItem('partnerUser');
      set({
        isLoading: false,
        isAuthenticated: false,
        error: error.message
      });
    }
  },

  // Login
  login: async (email, password) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.login(email, password);

      // La respuesta del endpoint de partners viene en response.data
      const loginData = response.data || response;

      if (!loginData.user?.partnerId) {
        throw new Error('Esta cuenta no está asociada a un partner');
      }

      localStorage.setItem('partnerToken', loginData.token);
      localStorage.setItem('partnerRefreshToken', loginData.refreshToken);
      localStorage.setItem('partnerUser', JSON.stringify(loginData.user));
      // La sesión 2FA es de la cuenta: la de quien entró antes en esta pestaña no sirve.
      useDosPasosStore.getState().borrarSesion();

      set({
        user: loginData.user,
        partner: loginData.partner,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      toast.success('Bienvenido al Portal de Partners');
      return true;
    } catch (error) {
      let message = error.response?.data?.message || error.response?.data?.error;

      // Si no hay mensaje del servidor, dar contexto según el status code
      if (!message) {
        const status = error.response?.status;
        if (status === 403) {
          message = 'Acceso denegado. Tu cuenta puede estar inactiva, suspendida o pendiente de aprobación. Contacta al administrador.';
        } else if (status === 401) {
          message = 'Credenciales inválidas. Verifica tu email y contraseña.';
        } else {
          message = error.message || 'Error al iniciar sesión';
        }
      }

      set({
        isLoading: false,
        error: message
      });
      toast.error(message);
      return false;
    }
  },

  // Logout
  logout: async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Ignore logout errors
    }

    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partnerRefreshToken');
    localStorage.removeItem('partnerUser');
    useDosPasosStore.getState().borrarSesion();

    set({
      user: null,
      partner: null,
      isAuthenticated: false,
      error: null
    });

    toast.success('Sesión cerrada correctamente');
  },

  // Update user profile
  updateProfile: async (data) => {
    try {
      const result = await authService.updateProfile(data);
      set((state) => ({
        user: { ...state.user, ...result.user },
        partner: result.partner || state.partner
      }));
      toast.success('Perfil actualizado correctamente');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al actualizar perfil');
      return false;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Get current user
  getUser: () => get().user,

  // Get current partner
  getPartner: () => get().partner,

  // Check if user has permission
  hasPermission: (permission) => {
    const { user, effectivePermissions } = get();
    if (effectivePermissions && effectivePermissions.length) {
      return effectivePermissions.includes(permission);
    }
    if (!user?.permissions) return false;
    return user.permissions.includes(permission);
  },

  // Refresca sesion (user, partner, teamMember, permisos efectivos) sin logout.
  // Util tras aceptar invitacion al team o cuando el backend emite
  // PERMISSION_REFRESH_NEEDED en una respuesta 403.
  refreshSession: async () => {
    try {
      const response = await teamGranularService.getMySession();
      const data = response?.data || response;
      if (!data?.user) return null;
      set((state) => ({
        user: { ...state.user, ...data.user },
        partner: data.partner ? { ...(state.partner || {}), ...data.partner } : state.partner,
        teamMember: data.teamMember || null,
        effectivePermissions: data.effectivePermissions || []
      }));
      return data;
    } catch (error) {
      console.warn('refreshSession failed:', error?.message);
      return null;
    }
  }
}));

// Initialize on load
if (typeof window !== 'undefined') {
  useAuthStore.getState().initialize();
  // El interceptor de api.js dispara este evento cuando el backend
  // responde 403 con code PERMISSION_REFRESH_NEEDED. Refrescamos sesion
  // de forma transparente para el usuario.
  window.addEventListener('partner:session-refresh-needed', () => {
    useAuthStore.getState().refreshSession();
  });
}

export { useAuthStore };
